/**
 * TypeScript type definitions for Bulk Recon Download feature
 * Backend handles ZIP creation, frontend handles selection and download coordination
 */

// ===== Core Domain Types =====

export interface Batch {
  id: number;
  employerId: string;
  status: BatchStatus;
  createdDate: Date;
  processedDate?: Date;
  fileName: string;
  fileSize: number;
  recordCount: number;
  downloadUrl?: string;
  isDownloadable: boolean;
}

export enum BatchStatus {
  Pending = 'PENDING',
  Processing = 'PROCESSING',
  Completed = 'COMPLETED',
  Failed = 'FAILED',
  Archived = 'ARCHIVED',
  Ready = 'READY'
}

// ===== Selection Management Types =====

export interface SelectionState {
  selectedBatchIds: Set<number>;
  selectAllState: SelectAllState;
  lastSelectedId?: number;
  totalSelectableCount: number;
}

export interface SelectAllState {
  checked: boolean;
  indeterminate: boolean;
}

// ===== API Request/Response Types =====

export interface BulkDownloadRequest {
  file_ids: number[];
  options?: {
    zip_name?: string;
    organize_by_employer?: boolean;
  };
}

// Response for ≥15 files (GCS URL)
export interface BulkDownloadResponse {
  download_url: string;
  expires_at: string;
  file_count: number;
  estimated_size_mb: number;
}

// For <15 files, response is direct ZIP blob (no JSON structure)

// ===== Backend Error Response Types =====

export interface ApiErrorResponse {
  error: string;
  details?: any;
  retry_after?: number;
  message?: string;
}

export interface FileSizeErrorDetails {
  invalid_files: Array<{
    file_id: number;
    file_name: string;
    size_mb: number;
    max_allowed_mb: number;
  }>;
}

export interface PermissionErrorDetails {
  inaccessible_files: number[];
}

export interface SizeLimitErrorDetails {
  total_size_mb: number;
  max_immediate_mb: number;
  message: string;
}

export interface RateLimitError extends ApiErrorResponse {
  error: "Maximum 2 concurrent downloads allowed";
  retry_after: number;
  message: string;
}

// ===== Component State Types =====

export interface DownloadState {
  inProgress: boolean;
  error?: string;
  lastRequestId?: string;
}

// ===== Hook Return Types =====

export interface BulkSelectionHook {
  selectedBatchIds: number[];
  selectedCount: number;
  selectBatch: (batchId: number) => void;
  selectAll: () => void;
  clearSelection: () => void;
  isSelected: (batchId: number) => boolean;
  selectAllState: SelectAllState;
  canDownload: boolean;
}

export interface BulkDownloadHook {
  downloadState: DownloadState;
  startDownload: (batchIds: number[]) => Promise<void>;
  resetDownload: () => void;
}

// ===== Component Prop Types =====

export interface BulkSelectionCheckboxProps {
  batchId: number;
  isSelected: boolean;
  onSelectionChange: (batchId: number) => void;
  disabled?: boolean;
  batch?: Batch;
}

export interface SelectAllCheckboxProps {
  selectAllState: SelectAllState;
  onSelectAll: () => void;
  disabled?: boolean;
  totalCount: number;
  selectedCount: number;
}

export interface BulkDownloadButtonProps {
  selectedBatchIds: number[];
  selectedCount: number;
  onDownload: (batchIds: number[]) => Promise<void>;
  downloadState: DownloadState;
  disabled?: boolean;
  className?: string;
}

// ===== Configuration Constants =====

export const BULK_DOWNLOAD_LIMITS = {
  MAX_BATCHES: 50,
  DOWNLOAD_TIMEOUT_MS: 120000,
} as const;

export const UI_CONSTANTS = {
  DEBOUNCE_DELAY_MS: 300,
  TOAST_DURATION_MS: 5000,
  LOADING_DELAY_MS: 100,
} as const;

// ===== Type Guards =====

export function isBulkDownloadResponse(response: unknown): response is BulkDownloadResponse {
  return (
    typeof response === 'object' &&
    response !== null &&
    'download_url' in response &&
    'expires_at' in response &&
    'file_count' in response &&
    'estimated_size_mb' in response
  );
}

export function isApiErrorResponse(response: unknown): response is ApiErrorResponse {
  return (
    typeof response === 'object' &&
    response !== null &&
    'error' in response
  );
}

// ===== Utility Types =====

export type BatchId = number;
export type SelectionAction = 'select' | 'deselect' | 'selectAll' | 'clearAll';
export type ToastType = 'success' | 'error' | 'warning' | 'info';